import { Fragment, useEffect, useId, useMemo, useRef, useState } from 'react'
import type { AccessApprovalCard, AccessPersonPage, AccessProblemItem, AccessProblemItems, AccessProblems, AccessScopeEdit, ApiFailure } from '../api/types'
import type { Api } from '../api'
import { Badge, Card, EmptyState, Loading, SearchInput } from '../components/ui'
import { FailureState } from '../components/FailureState'
import { Initials, STATUS, StatusBadge, dayTime, failureOf, messageOf } from './accessWords'
import type { PersonSection } from './AccessPeopleTab'

/**
 * Listed under Information, never counted as open: Waiting, which Approvals carries,
 * a main scope an admin changed on purpose (owner, 28 Oct 2026), and, since 30 Oct (D59),
 * personal settings and new manager links, which say how things are, not what is wrong.
 */
const NOT_COUNTED = new Set(['WAITING', 'MAIN_SCOPE_CHANGED', 'PERSONAL_SETTINGS', 'NEW_MANAGER_LINKS'])

/**
 * The tab's count (05 section 7.2): every open item but those NOT_COUNTED. Every other
 * category counts, gap warnings too (the owner's choice, 26 Oct 2026, over counting only what
 * an admin can fix here), and is listed under Problems.
 */
export function problemsCount(p: AccessProblems): number {
  return p.categories.filter((c) => !NOT_COUNTED.has(c.category)).reduce((n, c) => n + c.items, 0)
}

/**
 * ACCESS › PROBLEMS, under the new permission model (05 section 7.7; 03 rule 21).
 *
 * Everything that needs an admin, in one place, by category: each with how many it
 * holds, and its items in one shape (who or what it is about, one sentence, a
 * number, a time). The categories and their counts are the server's; an empty one
 * says "Nothing to fix here."
 *
 * Laid out again 27 Oct 2026 with the other Access pages: the categories as cards
 * with a count that says whether there is anything to do, the open total above
 * them, and the items as rows that open the person they are about, with a filter
 * over the items read.
 */
export function AccessProblemsTab({ api, onOpenPerson, onCount }: {
  api: Api
  onOpenPerson: (id: number, section?: PersonSection) => void
  /** The problems' items, as this tab last read them: the tab's count. */
  onCount?: (open: number) => void
}) {
  const [data, setData] = useState<AccessProblems | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [reload, setReload] = useState(0)
  const [picked, setPicked] = useState<string | null>(null)
  const [items, setItems] = useState<AccessProblemItems | null>(null)
  const [itemsFailure, setItemsFailure] = useState<ApiFailure | null>(null)
  // A filter over the items read, for this category only.
  const [filter, setFilter] = useState({ category: '', text: '' })
  // The person whose main scope is open beside Mindzer's (MAIN_SCOPE_CHANGED), in that category only.
  const [shown, setShown] = useState<{ category: string; id: number } | null>(null)

  useEffect(() => {
    let cancelled = false
    setFailure(null)
    api
      .accessProblems()
      .then((d) => {
        if (cancelled) return
        setData(d)
        onCount?.(problemsCount(d))
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setData(null)
        setFailure(failureOf(e))
      })
    return () => {
      cancelled = true
    }
  }, [api, reload, onCount])

  // Problems and information apart (owner, 30 Oct 2026): what counts in the tab's number, and
  // what is listed for information only.
  const problems = data?.categories.filter((c) => !NOT_COUNTED.has(c.category)) ?? []
  const info = data?.categories.filter((c) => NOT_COUNTED.has(c.category)) ?? []
  // The first problem with something in it, unless one was picked; then the information; with
  // nothing anywhere, the first problem.
  const current = picked ?? [...problems, ...info].find((c) => c.items > 0)?.category
    ?? problems[0]?.category ?? info[0]?.category ?? null
  const headId = useId()

  // Pages after the first, added below it (review R-20): staging's 3,705 sign-ins passed
  // the first 1,000 with no way on. More may follow while the last page came back full.
  const [more, setMore] = useState<{ page: number; full: boolean; busy: boolean; failure: string | null }>(
    { page: 1, full: false, busy: false, failure: null })
  const itemsGen = useRef(0)

  useEffect(() => {
    if (!current) return
    let cancelled = false
    const gen = ++itemsGen.current
    setItems(null)
    setItemsFailure(null)
    setMore({ page: 1, full: false, busy: false, failure: null })
    api
      .accessProblemItems(current)
      .then((d) => {
        if (cancelled || gen !== itemsGen.current) return
        setItems(d)
        setMore({ page: 1, full: d.items.length >= PROBLEMS_PAGE, busy: false, failure: null })
      })
      .catch((e: unknown) => !cancelled && setItemsFailure(failureOf(e)))
    return () => {
      cancelled = true
    }
  }, [api, current, reload])

  const showNext = () => {
    if (!current || !items || more.busy) return
    const gen = itemsGen.current
    const page = more.page + 1
    setMore((m) => ({ ...m, busy: true, failure: null }))
    api
      .accessProblemItems(current, page)
      .then((d) => {
        if (gen !== itemsGen.current) return
        // "Signed in, not in Mindzer" sorts by the last sign-in, which moves while the pages
        // are read: an item may come round twice, and is listed once (review of 17g).
        const key = (i: AccessProblemItem) => [i.category, i.mindzerUserId, i.code, i.compId, i.name, i.detail].join('\u0001')
        setItems((cur) => {
          if (!cur) return cur
          const have = new Set(cur.items.map(key))
          return { ...d, items: [...cur.items, ...d.items.filter((i) => !have.has(key(i)))] }
        })
        setMore({ page, full: d.items.length >= PROBLEMS_PAGE, busy: false, failure: null })
      })
      .catch((e: unknown) => {
        if (gen === itemsGen.current) setMore((m) => ({ ...m, busy: false, failure: messageOf(e) }))
      })
  }

  const meta = current ? CATEGORY[current] : null
  const text = filter.category === current ? filter.text : ''
  const rows = useMemo(() => {
    const s = text.trim().toLowerCase()
    if (!items || !s) return items?.items ?? []
    return items.items.filter((i) =>
      [i.name, detailWords(i), i.compId, i.code && codeWords(i.code, i.category), i.mindzerUserId !== null ? String(i.mindzerUserId) : null]
        .some((v) => v && v.toLowerCase().includes(s)))
  }, [items, text])
  const open = data ? problemsCount(data) : 0
  const withItems = problems.filter((c) => c.items > 0).length
  // A click shows the change in place, beside what Mindzer gives (owner, 28 Oct 2026).
  const inPlace = current === 'MAIN_SCOPE_CHANGED'
  const countsWhat = data?.categories.find((c) => c.category === current)?.countsWhat ?? ''

  return (
    <Card className="flex min-h-0 flex-1 flex-col">
      {failure ? (
        <FailureState failure={failure} onRetry={() => setReload((n) => n + 1)} />
      ) : data === null ? (
        <Loading label="Reading what needs an admin…" />
      ) : (
        <div className="grid min-h-0 flex-1 grid-cols-1 @5xl:grid-cols-[320px_1fr]">
          {/* Problems above, scrolling under their total; information pinned below them, so
              both are always in view (owner, 30 Oct 2026). */}
          <div className="flex min-h-0 flex-col border-(--color-line) bg-(--color-surface-2) @5xl:border-r">
            <div className="shrink-0 border-b border-(--color-line) px-4 py-3">
              <span id={`${headId}-problems`} className="eyebrow flex items-center gap-1.5">
                <WarnMark />Problems
              </span>
              <p className="text-(length:--text-small) text-(--color-ink-3)">
                <span className="text-(length:--text-xl) font-semibold text-(--color-ink) tabular-nums">{open.toLocaleString('en-GB')}</span>
                {' '}open in {withItems} {withItems === 1 ? 'category' : 'categories'}
              </p>
            </div>
            <ul className="flex min-h-0 flex-col gap-1.5 overflow-y-auto p-3" aria-labelledby={`${headId}-problems`}>
              {problems.map((c) => (
                <li key={c.category}>
                  <CategoryButton label={CATEGORY[c.category]?.label ?? c.category} items={c.items} on={c.category === current}
                    onPick={() => setPicked(c.category)} />
                </li>
              ))}
            </ul>
            {info.length > 0 && (
              /* On white, set off by the strong rule and its heading: on surface-3 the grey
                 text fell under 4.5:1 (review, 30 Oct 2026). */
              <div className="shrink-0 border-t border-(--color-line-strong) bg-(--color-surface) px-3 pt-2.5 pb-3">
                <div className="flex items-baseline gap-2 px-1 pb-1.5">
                  <span id={`${headId}-info`} className="eyebrow flex items-center gap-1.5"><InfoMark />Information</span>
                  <span className="ml-auto text-(length:--text-micro) text-(--color-ink-3)">not counted in the problems</span>
                </div>
                <ul className="flex flex-col gap-1" aria-labelledby={`${headId}-info`}>
                  {info.map((c) => (
                    <li key={c.category}>
                      <CategoryButton info label={CATEGORY[c.category]?.label ?? c.category} items={c.items} on={c.category === current}
                        onPick={() => setPicked(c.category)} />
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>

          <div className="flex min-h-0 flex-col">
            {current && (
              <div className="flex shrink-0 flex-col gap-2 border-b border-(--color-line) px-5 py-3">
                <div className="flex flex-wrap items-center gap-2.5">
                  <h3 className="text-(length:--text-lg) font-semibold text-(--color-ink)">{meta?.label ?? current}</h3>
                  {items && (
                    <Badge tone={(items.total ?? items.items.length) > 0 ? (NOT_COUNTED.has(current) ? 'neutral' : 'warn') : 'good'}>
                      {(items.total ?? items.items.length).toLocaleString('en-GB')} {countsWhat}
                    </Badge>
                  )}
                </div>
                {meta?.what && <p className="max-w-[80ch] text-(length:--text-small) leading-relaxed text-(--color-ink-2)">{meta.what}</p>}
                {items && items.items.length > 0 && (
                  <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                    <div className="w-72">
                      <SearchInput label="Filter these items" placeholder="Filter these: name, company, detail…" value={text}
                        onChange={(v) => setFilter({ category: current, text: v })} />
                    </div>
                    <span className="text-(length:--text-small) text-(--color-ink-3)">
                      {text.trim() ? `${rows.length.toLocaleString('en-GB')} of ${items.items.length.toLocaleString('en-GB')} listed match.` : ''}
                      {more.full && items.total !== null && items.total > items.items.length
                        ? ` Showing ${items.items.length.toLocaleString('en-GB')} of ${items.total.toLocaleString('en-GB')}.` : ''}
                    </span>
                    {more.full && items.total !== null && items.total > items.items.length && (
                      <button type="button" onClick={showNext} disabled={more.busy}
                        className="rounded px-1 text-(length:--text-small) font-medium text-(--color-navy-700) hover:underline disabled:opacity-60">
                        {more.busy ? 'Reading…' : `Show the next ${Math.min(items.total - items.items.length, PROBLEMS_PAGE).toLocaleString('en-GB')}`}
                      </button>
                    )}
                    {more.failure && <span className="text-(length:--text-small) font-medium text-(--color-overdue)">{more.failure}</span>}
                  </div>
                )}
              </div>
            )}
            {itemsFailure ? (
              <FailureState failure={itemsFailure} onRetry={() => setReload((n) => n + 1)} />
            ) : items === null ? (
              <Loading label="Reading this category…" />
            ) : items.items.length === 0 ? (
              <EmptyState title={current && NOT_COUNTED.has(current) ? 'Nothing here.' : 'Nothing to fix here.'} />
            ) : rows.length === 0 ? (
              <EmptyState title="Nothing matches the filter." detail="Clear it to see every item." />
            ) : (
              <div className="min-h-0 flex-1 overflow-auto">
                <table className="w-full border-collapse text-(length:--text-small)">
                  <thead className="sticky top-0 z-[1] bg-(--color-surface-2) text-left text-(--color-ink-3)">
                    <tr>
                      <th className="whitespace-nowrap px-5 py-2 font-medium">{items.items.some((i) => i.mindzerUserId !== null) ? 'User' : 'What'}</th>
                      <th className="whitespace-nowrap px-3 py-2 font-medium">Company</th>
                      <th className="px-3 py-2 font-medium">Detail</th>
                      <th className="whitespace-nowrap px-3 py-2 text-right font-medium">Count</th>
                      <th className="whitespace-nowrap px-3 py-2 font-medium">When</th>
                      <th className="px-5 py-2"><span className="sr-only">Open</span></th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((i, k) => {
                      const person = i.mindzerUserId
                      const name = i.name ?? (person !== null ? `Mindzer user ${person}` : '—')
                      const isShown = inPlace && person !== null && shown?.category === current && shown.id === person
                      return (
                        <Fragment key={`${person ?? i.code ?? ''}-${k}`}>
                        <tr
                          onClick={person === null ? undefined
                            : inPlace ? () => setShown(isShown ? null : { category: current!, id: person }) : () => onOpenPerson(person)}
                          aria-expanded={inPlace && person !== null ? isShown : undefined}
                          className={`border-t border-(--color-line) align-top transition-colors ${person !== null ? 'cursor-pointer hover:bg-(--color-navy-50)' : ''} ${isShown ? 'bg-(--color-navy-50)' : ''}`}
                        >
                          <td className="whitespace-nowrap px-5 py-2">
                            <span className="flex items-center gap-2.5">
                              {person !== null && <Initials name={name} size="sm" />}
                              <span className="flex flex-col">
                                <span className="font-medium text-(--color-ink)">{name}</span>
                                {i.code && (STATUS[i.code as keyof typeof STATUS]
                                  ? <span className="mt-0.5"><StatusBadge status={i.code as keyof typeof STATUS} /></span>
                                  : <span className="text-(--color-ink-3)">{codeWords(i.code, i.category)}</span>)}
                              </span>
                            </span>
                          </td>
                          <td className="whitespace-nowrap px-3 py-2 text-(--color-ink-2)">{i.compId ?? '—'}</td>
                          <td className="px-3 py-2 text-(--color-ink-2)">{detailWords(i) ?? '—'}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-right font-medium tabular-nums text-(--color-ink)">{i.countOf ?? ''}</td>
                          <td className="whitespace-nowrap px-3 py-2 text-(--color-ink-3)">{i.atUtc ? dayTime(i.atUtc) : ''}</td>
                          <td className="whitespace-nowrap px-5 py-2 text-right">
                            {person !== null && (inPlace ? (
                              // A real button: the row's click is the mouse's, this is the keyboard's (review of 18d).
                              <button type="button" aria-expanded={isShown}
                                onClick={(e) => { e.stopPropagation(); setShown(isShown ? null : { category: current!, id: person }) }}
                                className="rounded px-1 font-medium text-(--color-navy-700) hover:underline">
                                {isShown ? 'Hide ▴' : 'Show changes ▾'}
                              </button>
                            ) : (
                              <button type="button" onClick={(e) => { e.stopPropagation(); onOpenPerson(person) }}
                                className="rounded px-1 font-medium text-(--color-navy-700) hover:underline">
                                Open user →
                              </button>
                            ))}
                          </td>
                        </tr>
                        {isShown && (
                          <tr className="border-t border-(--color-line)">
                            <td colSpan={6} className="p-0">
                              <MainScopeChange api={api} id={person} onOpen={() => onOpenPerson(person, 'main')} />
                            </td>
                          </tr>
                        )}
                        </Fragment>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}
    </Card>
  )
}

/**
 * HR MAIN SCOPE CHANGED IN MBI, one person (owner, 28 Oct 2026): what Mindzer gives,
 * beside each line an admin added or took away, with who, when and why, and what
 * their main scope gives now. Read from their page (placement and edits) and their
 * card (the main scope's lines, as the builder made them), nothing new on the server.
 */
function MainScopeChange({ api, id, onOpen }: { api: Api; id: number; onOpen: () => void }) {
  const [read, setRead] = useState<{ page: AccessPersonPage; card: AccessApprovalCard } | null>(null)
  const [failure, setFailure] = useState<ApiFailure | null>(null)
  const [retry, setRetry] = useState(0)
  useEffect(() => {
    let cancelled = false
    setRead(null)
    setFailure(null)
    Promise.all([api.accessPerson(id), api.accessApprovalCard(id)])
      .then(([page, card]) => { if (!cancelled) setRead({ page, card }) })
      .catch((e: unknown) => { if (!cancelled) setFailure(failureOf(e)) })
    return () => { cancelled = true }
  }, [api, id, retry])

  if (failure) return <FailureState failure={failure} onRetry={() => setRetry((n) => n + 1)} />
  if (!read) return <Loading label="Reading their main scope…" />
  const p = read.page.person
  const placed = (kind: 'UNIT' | 'TEAM') => read.page.placement.filter((x) => x.kind === kind)
  // The main scope's lines as the builder made them, one each, whatever report they serve.
  const now = [...new Map(read.card.sees
    .filter((s) => s.scopeKey === 0 && s.compId !== null)
    .map((s) => [[s.compId, s.anyUnit, s.businessUnit, s.anyTeam, s.businessTeam, s.lineSource].join('|'), s] as const)).values()]
  return (
    <div className="flex flex-col gap-3 bg-(--color-surface-2) px-5 py-4">
      <div className="grid grid-cols-1 gap-4 @4xl:grid-cols-3">
        <section className="flex flex-col gap-2 rounded-md border border-(--color-line) bg-(--color-surface) px-4 py-3">
          <span className="eyebrow">From HR · {p?.compId ?? 'no company'}</span>
          {(['UNIT', 'TEAM'] as const).map((kind) => (
            // The label above its chips: "Business Teams" beside them would not fit this narrow card.
            <div key={kind} className="flex flex-col gap-1">
              <span className="text-(length:--text-micro) font-semibold uppercase tracking-[0.06em] text-(--color-ink-3)">{kind === 'UNIT' ? 'Business Units' : 'Business Teams'}</span>
              <div className="flex flex-wrap items-center gap-1.5">
                {placed(kind).length === 0 ? <span className="text-(--color-ink-4)">none</span> : placed(kind).map((x) => (
                  <span key={`${x.mindzerId}-${x.d365Code ?? ''}`} className="inline-flex items-center gap-1.5 rounded-full border border-(--color-line-strong) px-2 py-px">
                    <span className="text-(--color-ink)">{x.name ?? `#${x.mindzerId}`}</span>
                    {x.d365Code ? <span className="text-(--color-ink-3)">{x.d365Code}</span> : <span className="text-(--color-warn)">no D365 code</span>}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </section>
        <section className="flex flex-col gap-2 rounded-md border border-(--color-line) bg-(--color-surface) px-4 py-3">
          <span className="eyebrow">Changed in MBI · {read.page.edits.length}</span>
          {read.page.edits.length === 0 ? (
            <span className="text-(--color-ink-3)">No change any more: it was cleared since this list was read.</span>
          ) : (
            <ul className="flex flex-col gap-2">
              {read.page.edits.map((ed) => <ChangeLine key={ed.editId} edit={ed} />)}
            </ul>
          )}
        </section>
        <section className="flex flex-col gap-2 rounded-md border border-(--color-line) bg-(--color-surface) px-4 py-3">
          <span className="eyebrow">What their main scope gives now</span>
          {now.length === 0 ? (
            <span className="text-(--color-ink-3)">No rows: their main scope gives them no report yet.</span>
          ) : (
            <ul className="flex flex-col gap-1">
              {now.map((s) => (
                <li key={[s.compId, s.businessUnit, s.businessTeam, s.lineSource].join('|')} className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-(--color-ink)">{s.anyUnit ? 'All Business Units' : `Business Unit ${s.businessUnit ?? '?'}`} · {s.anyTeam ? 'all Business Teams' : `Business Team ${s.businessTeam ?? '?'}`}</span>
                  <span className="text-(length:--text-micro) text-(--color-ink-3)">{s.lineSource === 'A' ? 'added in MBI' : 'from HR'}</span>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>
      <div className="flex justify-end">
        <button type="button" onClick={onOpen} className="rounded px-1 text-(length:--text-small) font-medium text-(--color-navy-700) hover:underline">
          Open their HR main scope →
        </button>
      </div>
    </div>
  )
}

/** One line an admin set, in a sentence, and whether it acts now (HR main scope shows the same lines as a table). */
function ChangeLine({ edit: ed }: { edit: AccessScopeEdit }) {
  const what = !ed.anyTeam ? `Business Team ${ed.businessTeam}${!ed.anyUnit ? ` (in Business Unit ${ed.businessUnit})` : ''}`
    : !ed.anyUnit ? `Business Unit ${ed.businessUnit} (all Business Teams in it)` : 'Every Business Unit'
  // A paused remove still takes away (049); only a paused add waits.
  const idle = !ed.inMainCompany ? `For ${ed.compId ?? 'another company'}, not their main company`
    : ed.pausedUtc && ed.effect === 'A' ? `Paused: ${ed.pausedReason ?? 'no reason given'}` : null
  const add = ed.effect === 'A'
  return (
    <li className="flex items-start gap-2.5">
      <span aria-hidden className={`mt-0.5 inline-grid size-5 shrink-0 place-items-center rounded-full text-(length:--text-small) font-semibold ${
        add ? 'bg-(--color-good)/12 text-(--color-good)' : 'bg-(--color-overdue)/10 text-(--color-overdue)'}`}>{add ? '+' : '−'}</span>
      <span className="flex min-w-0 flex-col gap-0.5">
        <span className="flex flex-wrap items-center gap-1.5">
          <span className="text-(--color-ink)"><span className="font-semibold">{add ? 'Adds' : 'Takes away'}</span> {what}</span>
          {idle && <Badge tone="warn">No effect now</Badge>}
        </span>
        {idle && <span className="text-(length:--text-small) text-(--color-warn)">{idle}</span>}
        <span className="text-(length:--text-small) text-(--color-ink-3)">
          {[ed.changedByEmail, ed.changedUtc ? dayTime(ed.changedUtc) : null].filter(Boolean).join(' · ')}
          {ed.changeReason ? <> · <span className="italic">“{ed.changeReason}”</span></> : null}
        </span>
      </span>
    </li>
  )
}

/**
 * A badge code shows as its badge's words; any other code in words ("SIGN_IN_CONFLICT" → "Sign in
 * conflict"). A unit or team is always a Business Unit or Business Team (owner, 30 Oct 2026): Mindzer's
 * by its ID ("unit 88" → "Business Unit 88"). A D365 code (Codes not in D365, with its company) shows
 * exactly as D365 writes it: "LUB" read as words was "Lub" (review, 30 Oct 2026).
 */
function codeWords(code: string, category: string): string {
  const badge = STATUS[code as keyof typeof STATUS]?.label ?? WARNING_WORDS.get(code)
  if (badge) return badge
  const org = /^(unit|team) (\S+)$/i.exec(code)
  if (org) return `Business ${org[1].charAt(0).toUpperCase()}${org[1].slice(1).toLowerCase()} ${org[2]}`
  if (category === 'CODES_NOT_IN_D365') return code
  const words = code.split('_').filter(Boolean).map((w) => {
    const low = w.toLowerCase()
    return /\d/.test(w) ? w : /^(unit|team)s?$/.test(low) ? `Business ${low.charAt(0).toUpperCase()}${low.slice(1)}` : low
  }).join(' ')
  return words.charAt(0).toUpperCase() + words.slice(1)
}

/**
 * The gap warnings' codes (049 section 8) in screen words: Business Unit and Business Team,
 * HR Role "configured", "additional scope" (owner). A Map, so a D365 code such as
 * "constructor" finds nothing rather than an object's own function (review, 30 Oct 2026).
 */
const WARNING_WORDS = new Map<string, string>([
  ['NO_ROLE', 'No HR Role'],
  ['ROLE_NOT_SET_UP', 'HR Role not configured'],
  ['ROLE_NO_NAME', 'HR Role with no name'],
  ['NO_TEAM', 'No Business Team'],
  ['UNIT_WITHOUT_TEAM', 'Business Unit without Business Team'],
  ['UNIT_NO_D365_CODE', 'Business Unit with no D365 code'],
  ['UNIT_CODE_UNUSED', 'Business Unit code unused in D365'],
  ['TEAM_OUTSIDE_UNITS', 'Business Team outside their Business Units'],
  ['TEAM_NO_D365_CODE', 'Business Team with no D365 code'],
  ['TEAM_CODE_UNUSED', 'Business Team code unused in D365'],
  ['COMPANY_NO_D365_CODE', 'Company with no D365 code'],
  ['COMPANY_CODE_UNUSED', 'Company code unused in D365'],
  ['COMPANY_CODE_SHARED', 'D365 company on two Mindzer companies'],
  ['MANAGER_UNKNOWN', 'Manager not in Mindzer'],
  ['MANAGER_LOOP', 'Manager loop'],
  ['MANAGER_CHAIN_DEEP', 'Manager chain over 20 levels'],
  ['LINE_MANAGER_INACTIVE', 'Line manager inactive'],
  ['FUNC_MANAGER_INACTIVE', 'Functional manager inactive'],
  ['EXTRA_SCOPE_OVERLAP', 'Additional scope overlaps'],
  ['EXTRA_SCOPE_IN_MAIN_COMPANY', 'Additional scope in their main company'],
  ['EMAIL_BLANK', 'No email'],
  ['EMAIL_INVALID', 'Email not valid'],
  ['EMAIL_NOT_COMPANY', 'Not a company email'],
  ['EMAIL_REUSED', 'Email shared with someone else'],
  ['EMAIL_CHANGED_AFTER_APPROVAL', 'Email changed after approval'],
  ['DISABLED_IN_MINDZER', 'Disabled in Mindzer'],
  ['CONFIG_FOR_OLD_COMPANY', 'Settings for an earlier main company'],
  ['SYSTEM_ROLE_HOLDER_LEFT', 'System role held by a leaver'],
  ['SIGN_IN_CONFLICT', 'Sign-in conflict'],
  ['RETURNED', 'Back after leaving'],
])

/**
 * An item's detail as the screen says it. A gap warning's is the person's codes, as 052 joins
 * them ("NO_TEAM, UNIT_WITHOUT_TEAM x2"): each becomes its words, "x2" its count. A piece that
 * is not a code (the 400-character cut can end one mid-word) stays as sent.
 */
function detailWords(i: AccessProblemItem): string | null {
  if (i.category !== 'GAP_WARNINGS' || !i.detail) return i.detail
  return i.detail.split(', ').map((part) => {
    const m = /^([A-Z][A-Z0-9_]*)(?: x(\d+))?$/.exec(part)
    return m ? `${codeWords(m[1], i.category)}${m[2] ? ` ×${m[2]}` : ''}` : part
  }).join(', ')
}

/** The server's page of items (ProblemsAdminEndpoints.ItemsPage). */
const PROBLEMS_PAGE = 1000

/**
 * One category in the left list. A problem is a card with an amber count, or "✓ clear";
 * information is a quieter row with a navy count, on the white band pinned below them.
 */
function CategoryButton({ label, items, on, onPick, info = false }: {
  label: string
  items: number
  on: boolean
  onPick: () => void
  info?: boolean
}) {
  const look = on ? 'border-(--color-navy-700) bg-(--color-navy-50) ring-1 ring-(--color-navy-700)'
    : info ? 'border-transparent hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'
      : 'border-(--color-line) bg-(--color-surface) hover:border-(--color-navy-300) hover:bg-(--color-navy-50)'
  return (
    <button
      type="button"
      onClick={onPick}
      aria-current={on ? 'true' : undefined}
      className={`flex w-full items-center gap-3 rounded-md border px-3 text-left text-(length:--text-small) transition-colors ${info ? 'py-1.5' : 'py-2'} ${look}`}
    >
      <span className={`min-w-0 flex-1 ${items > 0 ? 'font-medium text-(--color-ink)' : 'text-(--color-ink-3)'}`}>{label}</span>
      {items > 0 ? (
        <span className={`inline-flex min-w-7 justify-center rounded-full px-2 text-(length:--text-micro) font-semibold leading-5 tabular-nums ${
          info ? 'bg-(--color-navy-100) text-(--color-navy-700)' : 'bg-(--color-warn)/12 text-(--color-warn)'}`}>
          {items.toLocaleString('en-GB')}
        </span>
      ) : info ? (
        <span className="text-(length:--text-micro) text-(--color-ink-3)">none</span>
      ) : (
        <span className="text-(length:--text-micro) font-semibold text-(--color-good)" title="Nothing to fix">✓ clear</span>
      )}
    </button>
  )
}

/** The marks beside the two headings: a warning triangle for problems, an "i" for information. */
function WarnMark() {
  return (
    <svg aria-hidden width="12" height="12" viewBox="0 0 16 16" fill="none" className="text-(--color-warn)">
      <path d="M8 1.8L15 14H1L8 1.8Z" stroke="currentColor" strokeWidth="1.6" strokeLinejoin="round" />
      <path d="M8 6.5V9.5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8" cy="11.7" r="0.9" fill="currentColor" />
    </svg>
  )
}

function InfoMark() {
  return (
    <svg aria-hidden width="12" height="12" viewBox="0 0 16 16" fill="none" className="text-(--color-navy-700)">
      <circle cx="8" cy="8" r="6.6" stroke="currentColor" strokeWidth="1.6" />
      <path d="M8 7.2V11.3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
      <circle cx="8" cy="4.9" r="0.9" fill="currentColor" />
    </svg>
  )
}

const CATEGORY: Record<string, { label: string; what: string }> = {
  WAITING: { label: 'Waiting for approval', what: 'Everyone undecided, exactly as Access approvals counts them. Approve from Access approvals, or from their page.' },
  GAP_WARNINGS: { label: 'Gap warnings', what: 'Everyone with a warning, approved or not.' },
  NO_HR_ROLE: { label: 'No HR Role', what: 'Active users Mindzer gives no HR Role. Mindzer must give them one.' },
  ROLES_NOT_SET_UP: { label: 'HR Roles not configured', what: 'Roles held by active users, with no settings in MBI. Configure them under HR Roles.' },
  CODES_NOT_IN_D365: { label: 'Codes not in D365', what: 'Mindzer Business Units and Business Teams with no D365 code, and D365 codes no Mindzer Business Unit or Business Team links. Ask Mindzer to link them.' },
  // "Overrides", as the person page's Manual Override and Override HR Role panels (owner, 2 Nov 2026).
  PERSONAL_SETTINGS: { label: 'Overrides', what: 'Everyone with an override or an MBI additional scope. For information: not counted in the problems.' },
  MAIN_SCOPE_CHANGED: {
    label: 'HR main scope changed in MBI',
    what: 'Everyone whose HR main scope an admin changed in MBI: a Business Unit or Business Team added, or one that HR gives taken away. Click a user to see what HR gives beside what MBI changed. For information: not counted in the problems.',
  },
  NEW_MANAGER_LINKS: { label: 'New manager links (7 days)', what: 'Manager links added or re-pointed this week, with how many users each manager gains. For information: not counted in the problems.' },
  SIGN_IN_CONFLICTS: { label: 'Sign-in conflicts', what: 'Sign-ins that cannot be linked by themselves, and links whose email no longer matches. Link by hand from the user’s page.' },
  SYSTEM_ROLE_LEFT: { label: 'System roles held by leavers', what: 'Nothing happens by itself: remove the system role by hand under Platform roles.' },
  HELD_UPDATES: { label: 'Held Mindzer updates', what: 'An update waiting for your review. Open Mindzer sync report.' },
  SIGNED_IN_NOT_IN_MINDZER: { label: 'Signed in, not in Mindzer', what: 'Sign-ins that match nobody in Mindzer. Ask HR to add them.' },
  PAUSED: { label: 'Paused settings', what: 'Paused additional scopes and lines, and lines left by an earlier main company.' },
  LEFT_OUT_BY_SYNC: { label: 'Left out by the sync', what: 'Placeholder and test accounts, and users with no usable company email.' },
  // R-23 (17f; 01 rule 20): counted once a day from D365's rows, one line per company.
  NO_SALESPERSON: {
    label: 'Rows with no salesperson',
    what: 'D365 rows with no salesperson: first the share over every company, then each company. Under hierarchy only users without hierarchy see these rows. Ask D365 to set one.',
  },
  UNKNOWN_SALESPERSON: {
    label: 'Salesperson not in Mindzer',
    what: 'D365 rows whose salesperson is nobody in Mindzer (users who left still count as known). Under hierarchy these rows reach no manager. Ask HR or D365 to fix the salesperson.',
  },
}
